import type {
  OperationProgressLifecycleState,
  OperationProgressMetric,
  OperationProgressModel,
  OperationProgressStep,
  OperationProgressStepStatus,
} from './operationProgress.types';
import {
  resolveSnapshotProgressVisibility,
  type RevitOperationProgressSnapshot,
} from '../../api/grpc/operationProgressClient';

export interface OperationProgressStepDefinition<Phase extends string = string> {
  id: Phase;
  label: string;
  detail?: string;
}

export function clampProgressCount(current: number | null | undefined, total: number): number {
  if (total <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(current ?? 0, total));
}

export function clampProgressPercent(percent: number | null | undefined): number | null {
  if (percent === null || percent === undefined || !Number.isFinite(percent)) {
    return null;
  }

  return Math.max(0, Math.min(100, Math.round(percent)));
}

export function isOperationProgressWaitingState(
  state: OperationProgressLifecycleState | null | undefined
): boolean {
  return state === 'waiting_for_revit' || state === 'waiting_for_user_input';
}

export function isNativeOwnedOperationProgress(operationType: string | undefined): boolean {
  return operationType === 'parameters.project.add'
    || operationType === 'parameters.project.undo'
    || operationType === 'parameters.family.add'
    || operationType === 'parameters.family.undo';
}

export function clampProgressStep(currentStep: number, totalSteps: number, minStep = 0): number {
  if (totalSteps <= 0 || !Number.isFinite(currentStep)) {
    return minStep;
  }

  const upperBound = Math.max(minStep, totalSteps);
  return Math.max(minStep, Math.min(currentStep, upperBound));
}

export function formatProgressStepLabel(
  currentStep: number,
  totalSteps: number,
  minStep = 0
): string {
  if (totalSteps <= 0) {
    return '';
  }

  return `Step ${clampProgressStep(currentStep, totalSteps, minStep)} of ${Math.max(
    minStep,
    totalSteps
  )}`;
}

function getOrderedStepStatus(
  stepIndex: number,
  currentStepIndex: number,
  complete: boolean,
  error: boolean
): OperationProgressStepStatus {
  if (complete || stepIndex < currentStepIndex) {
    return 'complete';
  }

  if (error && stepIndex === currentStepIndex) {
    return 'error';
  }

  if (stepIndex === currentStepIndex) {
    return 'current';
  }

  return 'pending';
}

export function buildOrderedProgressSteps<Phase extends string>(
  steps: Array<OperationProgressStepDefinition<Phase>>,
  currentStepIndex: number,
  options: { complete?: boolean; error?: boolean } = {}
): OperationProgressStep[] {
  const boundedStepIndex = clampProgressStep(currentStepIndex, Math.max(0, steps.length - 1), 0);

  return steps.map((step, index) => ({
    id: step.id,
    label: step.label,
    detail: step.detail,
    status: getOrderedStepStatus(index, boundedStepIndex, Boolean(options.complete), Boolean(options.error)),
  }));
}

export interface RevitOperationProgressModelDefaults {
  title: string;
  message?: string;
  detail?: string;
  completeMessage?: string;
  errorMessage?: string;
  metrics?: OperationProgressMetric[];
  locked?: boolean;
}

function mapLifecycleStateToProgressStatus(
  snapshot: RevitOperationProgressSnapshot
): OperationProgressModel['status'] {
  if (snapshot.failed || snapshot.state === 'failed') {
    return 'error';
  }

  if (snapshot.resultReady || snapshot.state === 'completed') {
    return 'complete';
  }

  if (isOperationProgressWaitingState(snapshot.state)) {
    return 'waiting';
  }

  if (snapshot.state === 'queued' || snapshot.state === 'unspecified') {
    return 'pending';
  }

  return 'running';
}

export function mapRevitOperationSnapshotToProgressModel(
  snapshot: RevitOperationProgressSnapshot,
  defaults: RevitOperationProgressModelDefaults
): OperationProgressModel {
  const progressVisibility = resolveSnapshotProgressVisibility(snapshot);
  const status = mapLifecycleStateToProgressStatus(snapshot);
  const message = snapshot.failed
    ? snapshot.error || defaults.errorMessage || snapshot.message || 'Operation failed'
    : snapshot.resultReady
      ? defaults.completeMessage || snapshot.message || 'Operation completed'
      : snapshot.message || defaults.message || defaults.title;
  const detailParts = [
    snapshot.waitReason,
    snapshot.currentItem,
    snapshot.detail,
    defaults.detail,
  ].filter((value): value is string => Boolean(value && value.trim()));

  return {
    operationId: snapshot.operationId,
    operationType: snapshot.operationType,
    title: defaults.title,
    message,
    detail: detailParts[0],
    status,
    phase: snapshot.phase,
    state: snapshot.state,
    functionName: snapshot.functionName,
    currentItem: snapshot.currentItem || undefined,
    waitReason: snapshot.waitReason || undefined,
    waitElapsedMs: snapshot.waitElapsedMs,
    activeElapsedMs: snapshot.activeElapsedMs,
    processed: snapshot.processed,
    total: snapshot.total,
    percent: progressVisibility.percent ?? null,
    elapsedMs: snapshot.elapsedMs,
    etaMs: progressVisibility.etaMs ?? undefined,
    cacheStatus: snapshot.cacheStatus,
    resultReady: snapshot.resultReady,
    error: snapshot.error || undefined,
    items: snapshot.items,
    metrics: defaults.metrics,
    locked: defaults.locked,
  };
}
